import React, { useState, useEffect } from 'react';
import api from './services/api';
import './AdminPage.css';
import Header from './components/Header';
import PasswordInput from './components/PasswordInput';
import AdminUsers from './components/Admin/AdminUsers';
import AdminCategories from './components/Admin/AdminCategories';
import AdminPrompts from './components/Admin/AdminPrompts';
import AdminPlans from './components/Admin/AdminPlans';
import SchedulerManagement from './components/SchedulerManagement';
import NotificationLogs from './components/NotificationLogs';
import AiUsageLogs from './components/AiUsageLogs';
import AdminSubscriptions from './components/Admin/AdminSubscriptions';
import AdminSystemInfo from './components/Admin/AdminSystemInfo';
import useConfigManager from './hooks/useConfigManager';

function AdminPage({ user, token, onNavigateHome, onNavigateSettings, onLogout, initialTab, onTabChange, onNavigateToPrompts, onNavigateToHome, stickyHeader }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'users');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Config managers for interface settings
  const smtp = useConfigManager('/admin/config/smtp', {
    host: '', port: '587', secure: true, user: '', password: '',
    fromAddress: '', rejectUnauthorized: true, isActive: false
  }, 'SMTP');
  const sendgrid = useConfigManager('/admin/config/sendgrid', {
    apiKey: '', fromAddress: '', fromName: 'Brings to Mind', isActive: false
  }, 'SendGrid');
  const twilio = useConfigManager('/admin/config/twilio', {
    accountSid: '', apiKeySid: '', apiKeySecret: '', authToken: '',
    phoneNumber: '', isActive: false
  }, 'Twilio');
  const openai = useConfigManager('/admin/config/openai', {
    apiKey: '', enabled: false, isActive: false,
    titleGenerationPrompt: '', followupGenerationPrompt: ''
  }, 'OpenAI');
  const appUrl = useConfigManager('/admin/config/app-url', {
    url: '', isActive: false
  }, 'App URL');

  // Config managers for system settings
  const defaultTheme = useConfigManager('/admin/config/default-theme', {
    theme: 'default', isActive: true
  }, 'Default theme');
  const inactivityThreshold = useConfigManager('/admin/config/inactivity-threshold', {
    weeks: 4, isActive: true
  }, 'Inactivity threshold');

  // Non-config state
  const [emailProvider, setEmailProvider] = useState('smtp');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState('');
  const [testSmsSending, setTestSmsSending] = useState(false);
  const [testTranscriptionSending, setTestTranscriptionSending] = useState(false);
  const [dbCleanupRunning, setDbCleanupRunning] = useState(false);
  const [dbCleanupResults, setDbCleanupResults] = useState(null);

  const changeTab = (tab) => {
    setActiveTab(tab);
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  useEffect(() => {
    if (activeTab === 'interfaces') {
      fetchEmailProvider();
      smtp.fetch();
      sendgrid.fetch();
      appUrl.fetch();
      twilio.fetch();
      openai.fetch();
    } else if (activeTab === 'system-settings') {
      defaultTheme.fetch();
      inactivityThreshold.fetch();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============== INTERFACE CONFIGURATION ==============

  const fetchEmailProvider = async () => {
    try {
      const response = await api.get('/admin/config/email-provider');
      setEmailProvider(response.data.provider || 'smtp');
    } catch (error) {
      console.error('Error fetching email provider:', error);
    }
  };

  const handleTestTranscription = async () => {
    setTestTranscriptionSending(true);

    try {
      const response = await api.post('/admin/config/openai/test', {});
      console.log('[TEST TRANSCRIPTION] Success:', response.data);
      console.log('[TEST TRANSCRIPTION] Transcription result:', response.data.transcription);
      alert('Transcription test successful! Check console for transcription result.');
    } catch (error) {
      console.error('[TEST TRANSCRIPTION] Error:', error);
      const errorMessage = error.response?.data?.error || error.message;
      alert('Transcription test failed: ' + errorMessage);
    } finally {
      setTestTranscriptionSending(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress) {
      alert('Please enter a test email address');
      return;
    }

    setTestEmailSending(true);

    try {
      await api.post('/admin/config/smtp/test', { testEmail: testEmailAddress });
      alert(`Test email sent successfully to ${testEmailAddress}`);
      setTestEmailAddress('');
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Failed to send test email: ' + (error.response?.data?.error || error.message));
    } finally {
      setTestEmailSending(false);
    }
  };

  const handleSaveEmailProvider = async (newProvider) => {
    try {
      await api.put('/admin/config/email-provider', { provider: newProvider });
      setEmailProvider(newProvider);
      alert(`Email provider switched to ${newProvider === 'smtp' ? 'Direct SMTP' : 'SendGrid'}`);
    } catch (error) {
      console.error('Error saving email provider:', error);
      alert('Failed to save email provider: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSendTestSms = async () => {
    if (!testSmsPhone) {
      alert('Please enter a phone number');
      return;
    }

    setTestSmsSending(true);

    try {
      await api.post('/admin/config/twilio/test', { testPhone: testSmsPhone });
      alert(`Test SMS sent successfully to ${testSmsPhone}`);
      setTestSmsPhone('');
    } catch (error) {
      console.error('Error sending test SMS:', error);
      alert('Failed to send test SMS: ' + (error.response?.data?.error || error.message));
    } finally {
      setTestSmsSending(false);
    }
  };

  // ============== SYSTEM SETTINGS ==============

  const handleDbCleanup = async () => {
    setDbCleanupRunning(true);
    setDbCleanupResults(null);

    try {
      const response = await api.post('/admin/db-cleanup', {});
      setDbCleanupResults(response.data.results);
    } catch (error) {
      console.error('Error running DB cleanup:', error);
      setDbCleanupResults([{
        step: 'Database Cleanup',
        status: 'failed',
        message: error.response?.data?.error || error.message
      }]);
    } finally {
      setDbCleanupRunning(false);
    }
  };

  return (
    <div className="admin-page">
      <Header
        user={user}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onSettingsClick={onNavigateSettings}
        onAdminClick={() => {}}
        onLogout={onLogout}
        isAdmin={true}
        onTitleClick={onNavigateHome}
        onNavigateToPrompts={onNavigateToPrompts}
        onNavigateToHome={onNavigateToHome}
        stickyHeader={stickyHeader}
      />

      <div className="admin-content">
        <nav className={`admin-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
          <button
            className="admin-sidebar-toggle"
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
          >
            {sidebarCollapsed ? '›' : '‹'}
          </button>
          {!sidebarCollapsed && (
            <>
              <button className={`admin-nav-item${activeTab === 'users' ? ' active' : ''}`} onClick={() => changeTab('users')}>Users</button>
              <button className={`admin-nav-item${activeTab === 'plans' ? ' active' : ''}`} onClick={() => changeTab('plans')}>Plans</button>
              <button className={`admin-nav-item${activeTab === 'categories' ? ' active' : ''}`} onClick={() => changeTab('categories')}>Categories</button>
              <button className={`admin-nav-item${activeTab === 'prompts' ? ' active' : ''}`} onClick={() => changeTab('prompts')}>Prompts</button>
              <button className={`admin-nav-item${activeTab === 'interfaces' ? ' active' : ''}`} onClick={() => changeTab('interfaces')}>Interfaces</button>
              <button className={`admin-nav-item${activeTab === 'system-settings' ? ' active' : ''}`} onClick={() => changeTab('system-settings')}>System Settings</button>
              <button className={`admin-nav-item${activeTab === 'scheduler' ? ' active' : ''}`} onClick={() => changeTab('scheduler')}>Scheduler</button>
              <button className={`admin-nav-item${activeTab === 'notification-logs' ? ' active' : ''}`} onClick={() => changeTab('notification-logs')}>Notification Logs</button>
              <button className={`admin-nav-item${activeTab === 'ai-usage' ? ' active' : ''}`} onClick={() => changeTab('ai-usage')}>AI Usage</button>
              <button className={`admin-nav-item${activeTab === 'subscriptions' ? ' active' : ''}`} onClick={() => changeTab('subscriptions')}>Subscriptions</button>
              <button className={`admin-nav-item${activeTab === 'system-info' ? ' active' : ''}`} onClick={() => changeTab('system-info')}>System Info</button>
            </>
          )}
        </nav>
        <div className="admin-main">

        {activeTab === 'users' && <AdminUsers timezone={user?.timezone} />}

        {activeTab === 'plans' && <AdminPlans />}

        {activeTab === 'categories' && <AdminCategories />}

        {activeTab === 'prompts' && <AdminPrompts timezone={user?.timezone} />}

        {activeTab === 'interfaces' && (
          <div className="admin-section">
            <h2>Interface Configuration</h2>

            <div className="admin-config-section" style={{ marginTop: 0 }}>
              <h3 className="admin-interface-header">
                Email Configuration
              </h3>

              {/* Email Provider Selection */}
              <div className="form-group admin-provider-selection">
                <label>Email Provider</label>
                <div className="admin-radio-group">
                  <label className="checkbox-label">
                    <input
                      type="radio"
                      name="emailProvider"
                      value="smtp"
                      checked={emailProvider === 'smtp'}
                      onChange={(e) => handleSaveEmailProvider(e.target.value)}
                    />
                    <span>Direct SMTP</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="radio"
                      name="emailProvider"
                      value="sendgrid"
                      checked={emailProvider === 'sendgrid'}
                      onChange={(e) => handleSaveEmailProvider(e.target.value)}
                    />
                    <span>SendGrid API</span>
                  </label>
                </div>
                <small className="admin-help-text">
                  Choose between direct SMTP server or SendGrid API for sending emails
                </small>
              </div>

              {/* SMTP Configuration Form */}
              {emailProvider === 'smtp' && (
                <form onSubmit={smtp.save} className="admin-form-narrow">
                  <div className="form-group">
                    <label className="checkbox-label admin-checkbox-inline">
                      <input
                        type="checkbox"
                        checked={smtp.config.isActive}
                        onChange={(e) => smtp.setConfig({ ...smtp.config, isActive: e.target.checked })}
                      />
                      <span>Enable Email Notifications</span>
                    </label>
                  </div>

                <div className="form-group">
                  <label>SMTP Host</label>
                  <input
                    type="text"
                    value={smtp.config.host}
                    onChange={(e) => smtp.setConfig({ ...smtp.config, host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    required={smtp.config.isActive}
                  />
                  <small className="admin-help-text">
                    Your email server hostname (e.g., smtp.gmail.com, smtp.office365.com)
                  </small>
                </div>

                <div className="form-group">
                  <label>SMTP Port</label>
                  <input
                    type="number"
                    value={smtp.config.port}
                    onChange={(e) => smtp.setConfig({ ...smtp.config, port: e.target.value })}
                    placeholder="587"
                    required={smtp.config.isActive}
                  />
                  <small className="admin-help-text">
                    Common ports: 587 (TLS), 465 (SSL), 25 (legacy)
                  </small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label admin-checkbox-inline no-margin">
                    <input
                      type="checkbox"
                      checked={smtp.config.secure}
                      onChange={(e) => smtp.setConfig({ ...smtp.config, secure: e.target.checked })}
                    />
                    <span>Use Secure Connection (TLS/SSL)</span>
                  </label>
                  <small className="admin-help-text block">
                    Recommended: Enable for port 465 or 587
                  </small>
                </div>

                <div className="form-group">
                  <label>SMTP Username</label>
                  <input
                    type="text"
                    value={smtp.config.user}
                    onChange={(e) => smtp.setConfig({ ...smtp.config, user: e.target.value })}
                    placeholder="your-email@example.com"
                    required={smtp.config.isActive}
                  />
                  <small className="admin-help-text">
                    Usually your full email address
                  </small>
                </div>

                <div className="form-group">
                  <label>SMTP Password</label>
                  <PasswordInput
                    name="smtpPassword"
                    value={smtp.config.password}
                    onChange={(e) => smtp.setConfig({ ...smtp.config, password: e.target.value })}
                    placeholder={smtp.config.password === '********' ? 'Password is set' : 'Enter password'}
                  />
                  <small className="admin-help-text">
                    For Gmail, use an App Password (not your regular password)
                  </small>
                </div>

                <div className="form-group">
                  <label>From Address</label>
                  <input
                    type="email"
                    value={smtp.config.fromAddress}
                    onChange={(e) => smtp.setConfig({ ...smtp.config, fromAddress: e.target.value })}
                    placeholder="noreply@yourdomain.com"
                  />
                  <small className="admin-help-text">
                    Optional: Email address to show in the "From" field. Defaults to username if not set.
                  </small>
                </div>

                <div className="admin-button-row">
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={smtp.saving}
                  >
                    {smtp.saving ? 'Saving...' : 'Save SMTP Configuration'}
                  </button>
                </div>
              </form>
              )}

              {/* SendGrid Configuration Form */}
              {emailProvider === 'sendgrid' && (
                <form onSubmit={sendgrid.save} className="admin-form-narrow">
                  <div className="form-group">
                    <label className="checkbox-label admin-checkbox-inline">
                      <input
                        type="checkbox"
                        checked={sendgrid.config.isActive}
                        onChange={(e) => sendgrid.setConfig({ ...sendgrid.config, isActive: e.target.checked })}
                      />
                      <span>Enable Email Notifications</span>
                    </label>
                  </div>

                  <div className="form-group">
                    <label>SendGrid API Key</label>
                    <PasswordInput
                      name="sendgridApiKey"
                      value={sendgrid.config.apiKey}
                      onChange={(e) => sendgrid.setConfig({ ...sendgrid.config, apiKey: e.target.value })}
                      placeholder={sendgrid.config.apiKey === '********' ? 'API key is set' : 'Enter SendGrid API key'}
                      required={sendgrid.config.isActive}
                    />
                    <small className="admin-help-text">
                      Get your API key from <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer">SendGrid Settings</a>
                    </small>
                  </div>

                  <div className="form-group">
                    <label>From Email Address</label>
                    <input
                      type="email"
                      value={sendgrid.config.fromAddress}
                      onChange={(e) => sendgrid.setConfig({ ...sendgrid.config, fromAddress: e.target.value })}
                      placeholder="noreply@yourdomain.com"
                      required={sendgrid.config.isActive}
                    />
                    <small className="admin-help-text">
                      This email address must be verified in your SendGrid account
                    </small>
                  </div>

                  <div className="form-group">
                    <label>From Name</label>
                    <input
                      type="text"
                      value={sendgrid.config.fromName}
                      onChange={(e) => sendgrid.setConfig({ ...sendgrid.config, fromName: e.target.value })}
                      placeholder="Brings to Mind"
                    />
                    <small className="admin-help-text">
                      The name that will appear in the "From" field of emails
                    </small>
                  </div>

                  <div className="admin-button-row">
                    <button
                      type="submit"
                      className="btn-save"
                      disabled={sendgrid.saving}
                    >
                      {sendgrid.saving ? 'Saving...' : 'Save SendGrid Configuration'}
                    </button>
                  </div>
                </form>
              )}

              {/* Test Email Section */}
              <div className="admin-test-divider">
                <h4>Test Email Configuration</h4>
                <div className="admin-test-row">
                  <div className="form-group">
                    <label>Test Email Address</label>
                    <input
                      type="email"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      placeholder="test@example.com"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleSendTestEmail}
                    className="btn-edit"
                    disabled={testEmailSending || (emailProvider === 'smtp' ? !smtp.config.isActive : !sendgrid.config.isActive)}

                  >
                    {testEmailSending ? 'Sending...' : 'Send Test Email'}
                  </button>
                </div>
                {((emailProvider === 'smtp' && !smtp.config.isActive) || (emailProvider === 'sendgrid' && !sendgrid.config.isActive)) && (
                  <small className="admin-help-text block">
                    Enable email notifications and save the configuration before sending test emails
                  </small>
                )}
              </div>
            </div>

            {/* SMS Configuration */}
            <div className="admin-config-section">
              <h3 className="admin-interface-header">
                SMS Configuration (Twilio)
              </h3>

              <form onSubmit={twilio.save} className="admin-form-narrow">
                <div className="form-group">
                  <label className="checkbox-label admin-checkbox-inline">
                    <input
                      type="checkbox"
                      checked={twilio.config.isActive}
                      onChange={(e) => twilio.setConfig({ ...twilio.config, isActive: e.target.checked })}
                    />
                    <span>Enable SMS Notifications</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Account SID</label>
                  <input
                    type="text"
                    value={twilio.config.accountSid}
                    onChange={(e) => twilio.setConfig({ ...twilio.config, accountSid: e.target.value })}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    required={twilio.config.isActive}
                  />
                  <small className="admin-help-text">
                    Your Twilio Account SID (starts with AC)
                  </small>
                </div>

                <div className="form-group">
                  <label>API Key SID (Recommended)</label>
                  <input
                    type="text"
                    value={twilio.config.apiKeySid}
                    onChange={(e) => twilio.setConfig({ ...twilio.config, apiKeySid: e.target.value })}
                    placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <small className="admin-help-text">
                    API Key SID for more secure authentication (starts with SK). Get from <a href="https://console.twilio.com/us1/develop/api-keys" target="_blank" rel="noopener noreferrer">Twilio Console</a>
                  </small>
                </div>

                <div className="form-group">
                  <label>API Key Secret</label>
                  <PasswordInput
                    name="twilioApiKeySecret"
                    value={twilio.config.apiKeySecret}
                    onChange={(e) => twilio.setConfig({ ...twilio.config, apiKeySecret: e.target.value })}
                    placeholder={twilio.config.apiKeySecret === '********' ? 'Secret is set' : 'Enter API key secret'}
                  />
                  <small className="admin-help-text">
                    The secret for your API Key (only shown once when created)
                  </small>
                </div>

                <div className="form-group">
                  <label>Auth Token (Alternative)</label>
                  <PasswordInput
                    name="twilioAuthToken"
                    value={twilio.config.authToken}
                    onChange={(e) => twilio.setConfig({ ...twilio.config, authToken: e.target.value })}
                    placeholder={twilio.config.authToken === '********' ? 'Token is set' : 'Enter auth token'}
                  />
                  <small className="admin-help-text">
                    Alternative to API Key: Your Twilio Auth Token (less secure)
                  </small>
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={twilio.config.phoneNumber}
                    onChange={(e) => twilio.setConfig({ ...twilio.config, phoneNumber: e.target.value })}
                    placeholder="+1234567890"
                    required={twilio.config.isActive}
                  />
                  <small className="admin-help-text">
                    Your Twilio phone number in E.164 format (e.g., +1234567890)
                  </small>
                </div>

                <div className="admin-button-row">
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={twilio.saving}
                  >
                    {twilio.saving ? 'Saving...' : 'Save Twilio Configuration'}
                  </button>
                </div>
              </form>

              {/* Test SMS Section */}
              <div className="admin-test-divider">
                <h4>Test SMS Configuration</h4>
                <div className="admin-test-row">
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={testSmsPhone}
                      onChange={(e) => setTestSmsPhone(e.target.value)}
                      placeholder="+1234567890"
                    />
                    <small className="admin-help-text">
                      Enter phone number in E.164 format (e.g., +1234567890)
                    </small>
                  </div>
                  <button
                    type="button"
                    onClick={handleSendTestSms}
                    className="btn-edit"
                    disabled={testSmsSending || !twilio.config.isActive}

                  >
                    {testSmsSending ? 'Sending...' : 'Send Test SMS'}
                  </button>
                </div>
                {!twilio.config.isActive && (
                  <small className="admin-help-text block">
                    Enable SMS notifications and save the configuration before sending test messages
                  </small>
                )}
              </div>
            </div>

            {/* App URL Configuration */}
            <div className="admin-config-section">
              <h3 className="admin-interface-header">
                Application URL
              </h3>
              <p className="admin-section-description">
                Configure the base URL for your application. This is used in email invitation links and redirects.
              </p>
              <form onSubmit={appUrl.save}>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label className="checkbox-label admin-checkbox-inline">
                    <input
                      type="checkbox"
                      checked={appUrl.config.isActive}
                      onChange={(e) => appUrl.setConfig({ ...appUrl.config, isActive: e.target.checked })}
                    />
                    <span style={{ marginLeft: '0.5rem' }}>Enable custom app URL (overrides environment variable)</span>
                  </label>

                  <label className="form-label">
                    Application URL
                    <input
                      type="url"
                      value={appUrl.config.url}
                      onChange={(e) => appUrl.setConfig({ ...appUrl.config, url: e.target.value })}
                      placeholder="https://bringstomind.com"
                      required={appUrl.config.isActive}
                      disabled={!appUrl.config.isActive}
                    />
                  </label>
                  <small className="admin-help-text block">
                    Example: https://bringstomind.com or http://localhost:3001
                  </small>
                </div>

                <button
                  type="submit"
                  className="button button-primary"
                  disabled={appUrl.saving || !appUrl.config.isActive}
                >
                  {appUrl.saving ? 'Saving...' : 'Save App URL Configuration'}
                </button>
              </form>
            </div>

            {/* OpenAI Service */}
            <div className="admin-config-section">
              <h3 className="admin-interface-header">
                OpenAI Service
              </h3>
              <p className="admin-section-description">
                Configure OpenAI API access for AI-powered features including transcription and title generation.
              </p>

              <form onSubmit={openai.save} className="admin-form-narrow">
                <div className="form-group">
                  <label>OpenAI API Key</label>
                  <PasswordInput
                    name="openaiApiKey"
                    value={openai.config.apiKey}
                    onChange={(e) => openai.setConfig({ ...openai.config, apiKey: e.target.value })}
                    placeholder={openai.config.apiKey === '********' ? 'API key is set' : 'Enter OpenAI API key (sk-...)'}
                  />
                  <small className="admin-help-text">
                    Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">OpenAI Platform</a>.
                  </small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label admin-checkbox-inline compact">
                    <input
                      type="checkbox"
                      checked={openai.config.enabled}
                      onChange={(e) => openai.setConfig({ ...openai.config, enabled: e.target.checked, isActive: e.target.checked })}
                    />
                    <span>Enable OpenAI Service</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Title Generation Prompt</label>
                  <textarea
                    value={openai.config.titleGenerationPrompt}
                    onChange={(e) => openai.setConfig({ ...openai.config, titleGenerationPrompt: e.target.value })}
                    placeholder="Enter the system prompt for title generation..."
                    disabled={!openai.config.enabled}
                    rows={5}
                    className="admin-textarea-full"
                  />
                  <small className="admin-help-text">
                    This prompt is sent to the AI when generating titles for reflections. Leave empty to use the default prompt.
                  </small>
                </div>

                <div className="form-group">
                  <label>Follow-up Questions Prompt</label>
                  <textarea
                    value={openai.config.followupGenerationPrompt}
                    onChange={(e) => openai.setConfig({ ...openai.config, followupGenerationPrompt: e.target.value })}
                    placeholder="Enter the system prompt for follow-up question generation..."
                    disabled={!openai.config.enabled}
                    rows={5}
                    className="admin-textarea-full"
                  />
                  <small className="admin-help-text">
                    This prompt is sent to the AI when generating follow-up questions for reflections. Leave empty to use the default prompt.
                  </small>
                </div>

                <div className="admin-button-row">
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={openai.saving}
                  >
                    {openai.saving ? 'Saving...' : 'Save OpenAI Configuration'}
                  </button>
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={handleTestTranscription}
                    disabled={testTranscriptionSending || !openai.config.enabled}
                    title={!openai.config.enabled ? 'Enable OpenAI service first' : 'Test transcription with a sample audio file'}
                  >
                    {testTranscriptionSending ? 'Testing...' : 'Test Transcription'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'system-settings' && (
          <div className="admin-section">
            <h2>System Settings</h2>

            {/* Default Theme Setting */}
            <div className="admin-settings-section">
              <h3 className="admin-settings-header">
                Default Theme for New Accounts
              </h3>
              <p className="admin-section-description">
                Choose the default theme that will be applied to new user accounts when they register.
              </p>

              <form onSubmit={defaultTheme.save} className="admin-form-narrow">
                <div className="form-group">
                  <label className="checkbox-label admin-checkbox-inline">
                    <input
                      type="checkbox"
                      checked={defaultTheme.config.isActive}
                      onChange={(e) => defaultTheme.setConfig({ ...defaultTheme.config, isActive: e.target.checked })}
                    />
                    <span>Use system default theme (if disabled, users will get 'default' theme)</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Default Theme</label>
                  <select
                    value={defaultTheme.config.theme}
                    onChange={(e) => defaultTheme.setConfig({ ...defaultTheme.config, theme: e.target.value })}
                    disabled={!defaultTheme.config.isActive}
                  >
                    <option value="default">🧠 Default</option>
                    <option value="ocean">🌊 Ocean</option>
                    <option value="sunset">🌅 Sunset</option>
                    <option value="forest">🌲 Forest</option>
                    <option value="midnight">🌙 Midnight</option>
                    <option value="mindful">🧘 Mindful Minimalism</option>
                    <option value="zen">🌿 Zen Garden</option>
                    <option value="focus">🎯 Focus Flow</option>
                    <option value="warmWelcome">☀️ Warm Welcome</option>
                  </select>
                  <small className="admin-help-text block">
                    New users will start with this theme. They can change it later in their preferences.
                  </small>
                </div>

                <div className="admin-button-row">
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={defaultTheme.saving}
                  >
                    {defaultTheme.saving ? 'Saving...' : 'Save Default Theme'}
                  </button>
                </div>
              </form>
            </div>

            {/* Inactivity Threshold Section */}
            <div className="admin-settings-section">
              <h3 className="admin-settings-header">
                Welcome Back Threshold
              </h3>
              <p className="admin-section-description">
                Configure how long a user must be inactive before showing the "Welcome Back" dialog when they return.
              </p>

              <form onSubmit={inactivityThreshold.save} className="admin-form-narrow">
                <div className="form-group">
                  <label className="checkbox-label admin-checkbox-inline">
                    <input
                      type="checkbox"
                      checked={inactivityThreshold.config.isActive}
                      onChange={(e) => inactivityThreshold.setConfig({ ...inactivityThreshold.config, isActive: e.target.checked })}
                    />
                    <span>Enable Welcome Back dialog for inactive users</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Inactivity Threshold (weeks)</label>
                  <input
                    type="number"
                    value={inactivityThreshold.config.weeks}
                    onChange={(e) => inactivityThreshold.setConfig({ ...inactivityThreshold.config, weeks: parseInt(e.target.value) || 1 })}
                    min="1"
                    max="52"
                    disabled={!inactivityThreshold.config.isActive}
                    className="admin-input-narrow"
                  />
                  <small className="admin-help-text block">
                    Users who haven't been active for this many weeks will see a "Welcome Back" dialog.
                    Set to 1-52 weeks.
                  </small>
                </div>

                <div className="admin-button-row">
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={inactivityThreshold.saving}
                  >
                    {inactivityThreshold.saving ? 'Saving...' : 'Save Inactivity Threshold'}
                  </button>
                </div>
              </form>
            </div>

            {/* DB Cleanup Section */}
            <div className="admin-settings-section">
              <h3 className="admin-settings-header">
                Database Cleanup
              </h3>
              <p className="admin-section-description">
                Run maintenance tasks to fix common database issues.
              </p>

              <div className="admin-form-narrow">
                <button
                  className="btn-save"
                  onClick={handleDbCleanup}
                  disabled={dbCleanupRunning}
                >
                  {dbCleanupRunning ? 'Running...' : 'DB Cleanup'}
                </button>

                {dbCleanupResults && (
                  <div className="admin-results-box">
                    <h4>Cleanup Results:</h4>
                    <ul className="admin-results-list">
                      {dbCleanupResults.map((result, index) => (
                        <li key={index} className="admin-results-item">
                          <span className={`admin-results-status ${result.status === 'done' ? 'success' : 'failed'}`}>
                            {result.status === 'done' ? (result.count !== undefined ? `[${result.count}]` : '[done]') : '[failed]'}
                          </span>
                          <span>
                            <strong>{result.step}:</strong> {result.message}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="admin-cleanup-info">
                  <strong>Cleanup steps performed:</strong>
                  <ul>
                    <li>Reset stuck transcriptions (processing → none)</li>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        )}

      {activeTab === 'scheduler' && (
        <SchedulerManagement token={token} timezone={user?.timezone} />
      )}

      {activeTab === 'notification-logs' && (
        <NotificationLogs token={token} timezone={user?.timezone} />
      )}

      {activeTab === 'ai-usage' && (
        <AiUsageLogs token={token} timezone={user?.timezone} />
      )}

      {activeTab === 'subscriptions' && (
        <div className="admin-section">
          <h2>Subscriptions</h2>
          <AdminSubscriptions />
        </div>
      )}

      {activeTab === 'system-info' && (
        <div className="admin-section">
          <h2>System Info</h2>
          <AdminSystemInfo />
        </div>
      )}
        </div>{/* admin-main */}
      </div>{/* admin-content */}
    </div>
  );
}

export default AdminPage;
